
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import moment from 'moment';
import { PlanSection, PlanStatus } from '@/types/billing-types';

export default defineComponent({
  name: 'PlanHeroCard',

  components: { Button, SpmButton },

  props: {
    plan: {
      type: Object as PropType<PlanSection>,
      required: true,
    },
  },

  emits: [
    'change-plan',
    'cancel',
    'undo-cancel',
    'extend-trial',
    'undo-downgrade',
    'manage-auto-exclusion',
  ],

  setup(props) {
    const { t } = useI18n();

    const status = computed(() => props.plan.status);
    const isCancelScheduled = computed(() => props.plan.isCancelScheduled);
    const canCancel = computed(() => props.plan.canCancel);
    const canUpgrade = computed(() => props.plan.canUpgrade);

    const nextPlanName = computed(() => {
      if (!props.plan.nextPlan) return '';
      // Reuse the i18n keys for plan names (billing.compare.planName.<code>).
      return t(`billing.compare.planName.${props.plan.nextPlan}`);
    });

    const eyebrow = computed(() => {
      switch (props.plan.status) {
        case 'none':
        // `cancelled` = had a plan, now `is_active = 0`. Same UX as
        // `none` here: no offer, the user must choose one. Without
        // this case the eyebrow falls to the default "OFFRE
        // ACTUELLE" which is misleading.
        case 'cancelled':
          return t('billing.subscriptionPage.hero.eyebrowNone');
        case 'trial':
        case 'trial_expiring_soon':
          return t('billing.subscriptionPage.hero.eyebrowTrial');
        case 'pending_payment':
        case 'pending_capped_increase':
          return t('billing.subscriptionPage.hero.eyebrowPending');
        default:
          return t('billing.subscriptionPage.hero.eyebrowCurrent');
      }
    });

    const title = computed(() => (
      props.plan.name ?? t('billing.subscriptionPage.hero.noPlanTitle')
    ));

    const subtitle = computed(() => {
      if (props.plan.status === 'none' || props.plan.status === 'cancelled') {
        return t('billing.subscriptionPage.hero.subtitleNoPlan');
      }
      // Trial countdown — defensive: also covers the case where the
      // backend didn't populate `daysUntilTrialEnd` but the status is
      // clearly a trial one. Without the fallback the user gets a string
      // like "Plus que  jour(s)..." (no number) which looks like a bug.
      if (
        props.plan.status === 'trial'
        || props.plan.status === 'trial_expiring_soon'
        || props.plan.statusContext?.daysUntilTrialEnd !== undefined
      ) {
        const raw = props.plan.statusContext?.daysUntilTrialEnd;
        const days = Number.isFinite(raw as number)
          ? Math.max(0, Number(raw))
          : 0;
        return t('billing.subscriptionPage.hero.subtitleTrial', { days });
      }
      if (props.plan.status === 'unsubscribe' && props.plan.nextRenewalAt) {
        return t('billing.subscriptionPage.hero.subtitleUnsubscribe', {
          date: moment(props.plan.nextRenewalAt).format('DD/MM/YYYY'),
        });
      }
      return '';
    });

    const badge = computed(() => {
      const s = props.plan.status;
      const map: Partial<Record<PlanStatus, { label: string; tone: string }>> = {
        trial: { label: t('billing.subscriptionPage.hero.badge.trial'), tone: 'info' },
        trial_expiring_soon: {
          label: t('billing.subscriptionPage.hero.badge.trialEnding'),
          tone: 'warning',
        },
        active: { label: t('billing.subscriptionPage.hero.badge.active'), tone: 'success' },
        unsubscribe: {
          label: t('billing.subscriptionPage.hero.badge.cancelling'),
          tone: 'warning',
        },
        pending_payment: {
          label: t('billing.subscriptionPage.hero.badge.pending'),
          tone: 'warning',
        },
        pending_capped_increase: {
          label: t('billing.subscriptionPage.hero.badge.pending'),
          tone: 'warning',
        },
        payment_failed_first: {
          label: t('billing.subscriptionPage.hero.badge.unpaid'),
          tone: 'danger',
        },
        payment_failed_second: {
          label: t('billing.subscriptionPage.hero.badge.unpaid'),
          tone: 'danger',
        },
      };
      return map[s] ?? null;
    });

    const priceLabel = computed(() => (
      props.plan.pricePaid?.label
        ?? props.plan.price?.label
        ?? null
    ));

    const frequencyLabel = computed(() => {
      if (props.plan.frequency === 'yearly') return t('billing.subscriptionPage.hero.year');
      if (props.plan.frequency === 'monthly') return t('billing.subscriptionPage.hero.month');
      return null;
    });

    const nextRenewalAt = computed(() => props.plan.nextRenewalAt);
    const formattedNextRenewal = computed(() => (
      nextRenewalAt.value ? moment(nextRenewalAt.value).format('DD MMMM YYYY') : '—'
    ));

    // Headline usage stat shown in the hero: total pages viewed (v3/v4) or
    // total active contacts (v5). Driven by plan.metricTotal which the
    // backend computes for every offer version.
    const metricStat = computed(() => {
      const m = props.plan.metricTotal;
      if (!m) return null;
      return {
        kind: m.kind,
        value: new Intl.NumberFormat('fr-FR').format(m.value),
        label:
          m.kind === 'contacts'
            ? t('billing.subscriptionPage.hero.metricContacts')
            : t('billing.subscriptionPage.hero.metricPagesViewed'),
        // V5 : nombre de contacts non-facturables (is_billable=0) — complément
        // des contacts actifs, cohérent avec le badge de « Mes contacts ».
        nonBillable:
          m.kind === 'contacts' && m.nonBillable && m.nonBillable > 0
            ? new Intl.NumberFormat('fr-FR').format(m.nonBillable)
            : null,
      };
    });

    // Catalog price shown struck-through when the shop actually pays less than
    // the catalog price (negotiated discount / plan_price ceiling / proration).
    const catalogStrikeLabel = computed<string | null>(() => {
      const { price, pricePaid } = props.plan;
      if (price && pricePaid && pricePaid.amount < price.amount) {
        return price.label;
      }
      return null;
    });

    return {
      t,
      status,
      isCancelScheduled,
      canCancel,
      canUpgrade,
      nextPlanName,
      eyebrow,
      title,
      subtitle,
      badge,
      priceLabel,
      frequencyLabel,
      nextRenewalAt,
      formattedNextRenewal,
      metricStat,
      catalogStrikeLabel,
    };
  },
});
